// Libraries
import * as React from 'react';
import { observer } from 'mobx-react';
import { observable, computed, action } from 'mobx';
import { unformat } from 'accounting';
import { CSSTransitionGroup } from 'react-transition-group';

// Styles
require('./LoadingSpinner.css');
const c = 'comp-spinner';

// Interfaces
interface IArcDimensions {
    sizeHalf: number;
    strokeWidth: number;
    arcRadius: number;
    arcDescription: string;
}

// Props
interface ILoadingSpinnerProps {
    color?: string;
    size?: number;
    active: boolean;
    className?: string | null;
}

// Component
@observer
export class LoadingSpinner extends React.Component<ILoadingSpinnerProps, {}> {
    static get defaultProps(): ILoadingSpinnerProps {
        return {
            size: 64,
            color: '#A0A3A3',
            active: false,
            className: null,
        };
    }

    constructor(props: ILoadingSpinnerProps) {
        super(props);
    }

    @computed private get wrapperClassName(): string {
        let className = `${c}-wrapper`;
        className += this.props.className ? ' ' + this.props.className : '';
        return className;
    }

    @computed private get arcDimensions(): IArcDimensions {
        // Calculate base values
        const sizeHalf = this.props.size / 2;
        const strokeWidth = this.props.size / 10;
        const arcRadius = sizeHalf - strokeWidth;

        // Return arc dimensions
        return {
            sizeHalf, strokeWidth, arcRadius,
            arcDescription: this.describeArc(sizeHalf, sizeHalf, arcRadius, -45, 45),
        };
    }

    public render() {
        return (
            <div
                ref={this.handleWrapperReference}
                className={this.wrapperClassName}
                style={{ width: this.props.size + 'px', height: this.props.size + 'px' }}
            >
                <CSSTransitionGroup
                    component="div"
                    className={`${c}-container`}
                    transitionName="spin"
                    transitionAppear={true}
                    transitionAppearTimeout={200}
                    transitionEnter={true}
                    transitionEnterTimeout={200}
                    transitionLeave={false}
                    transitionLeaveTimeout={0}
                >
                    {this.props.active && (
                        <svg
                            key="spinner"
                            className={c}
                            width={this.props.size}
                            height={this.props.size}
                        >
                            <path
                                fill="none"
                                stroke={this.props.color}
                                strokeWidth={this.arcDimensions.strokeWidth}
                                d={this.arcDimensions.arcDescription}
                            />
                            <circle
                                cx={this.arcDimensions.sizeHalf}
                                cy={this.arcDimensions.sizeHalf}
                                r={this.arcDimensions.arcRadius}
                                fill="none"
                                stroke={this.props.color}
                                strokeWidth={this.arcDimensions.strokeWidth}
                                opacity={0.2}
                            />
                        </svg>
                    )}
                </CSSTransitionGroup>
            </div>
        );
    }

    public spinnerWrapper: HTMLDivElement;
    private handleWrapperReference = (ref: HTMLDivElement) => this.spinnerWrapper = ref;

    private polarToCartesian(centerX: number, centerY: number, radius: number, angleInDegrees: number): {
        x: number,
        y: number
    } {
        const angleInRadians = (angleInDegrees - 90) * Math.PI / 180;

        return {
            x: centerX + (radius * Math.cos(angleInRadians)),
            y: centerY + (radius * Math.sin(angleInRadians)),
        };
    }

    private describeArc(x: number, y: number, radius: number, startAngle: number, endAngle: number): string {
        const start = this.polarToCartesian(x, y, radius, endAngle);
        const end = this.polarToCartesian(x, y, radius, startAngle);

        const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';

        const d = [
            'M', start.x, start.y,
            'A', radius, radius, 0, largeArcFlag, 0, end.x, end.y,
        ].join(' ');

        return d;
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/components/indicator/LoadingSpinner.tsx